import { Box, Text } from "@chakra-ui/react";

import { REPORT_TYPE_IDS } from "../../store/constants";
import { formatNumber, formatTime } from "../../store/helpers";
import { resolveAgentServiceTicketCount } from "../../store/agentServiceTimeMetrics";
import ConfiguredReportTable from "./ConfiguredReportTable";

const REPORT_KEYS_BY_ID = Object.freeze(
  Object.fromEntries(Object.entries(REPORT_TYPE_IDS).map(([key, id]) => [id, key]))
);

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const AGENT_SERVICE_COLUMN = Object.freeze({
  id: "agentService",
  label: "Agent / Service",
  pinned: true,
});

const pickNumber = (...values) => {
  const value = values.find((item) => item !== undefined && item !== null && item !== "");
  return Number(value) || 0;
};

const pct = (value) => {
  if (value === undefined || value === null || value === "") return "";
  return `${Number(value || 0).toFixed(2)}%`;
};

const withScope = (row, scopeName, branchName, serviceName) => ({
  ...row,
  scopeName: row.scopeName || scopeName || branchName || "Entire Organization",
  branchName: row.branchName || branchName || "",
  serviceName: row.serviceName || row.name || serviceName || "",
  completed: row.completed ?? row.numCompleted ?? row.totalCompleted,
  noShow: row.noShow ?? row.numNoShow ?? row.totalNoShow,
  unattended: row.unattended ?? row.numUnattended ?? row.totalUnattended,
  total: row.total ?? row.totalTickets ?? row.totalVisitors,
  average:
    row.average ??
    row.avg ??
    row.averageTicketsPerHour ??
    row.averageTicketsPerDay ??
    row.avgWaitingTimePerTicket,
  averagePerDay: row.averagePerDay ?? row.avgWaitingTimePerDay ?? row.avgWaitingTimePerHour,
  min: row.min ?? row.minTicketsPerHour ?? row.minTicketsPerDay ?? row.minWaitingTimePerHour,
  max: row.max ?? row.maxTicketsPerHour ?? row.maxTicketsPerDay ?? row.maxWaitingTimePerHour,
  totalServiceTime: row.totalServiceTime ?? row.sum ?? row.sessionInSeconds,
  totalWaitingTime: row.totalWaitingTime ?? row.waitingTimeInSeconds,
  ticketCount: row.ticketCount ?? row.completedTickets ?? row.numCompleted ?? row.completed,
});

const getBranchServiceRows = (data) =>
  asArray(data).flatMap((branch) =>
    asArray(branch.servicesData || branch.serviceData).map((service) =>
      withScope(service, branch.branchName, branch.branchName, service.serviceName || service.name)
    )
  );

const getBranchAgentServiceRows = (data) =>
  asArray(data).flatMap((branch) =>
    asArray(branch.servicesData).flatMap((service) => {
      const agents = asArray(service.agentsData);
      const serviceTotalTickets =
        service.completedTickets ??
        service.ticketCount ??
        agents.reduce((total, agent) => total + resolveAgentServiceTicketCount(agent), 0);
      const serviceTotalTime =
        service.totalServiceTime ??
        service.sum ??
        agents.reduce((total, agent) => total + pickNumber(agent.totalServiceTime, agent.sum), 0);
      const serviceAverage =
        service.serviceAverage ??
        service.averageServiceTime ??
        service.avgPerTicket ??
        (serviceTotalTickets ? serviceTotalTime / serviceTotalTickets : 0);

      return agents.map((agent) => {
        const agentAverage = pickNumber(agent.avgPerTicket, agent.average);
        return withScope(
          {
            ...agent,
            completedTickets: resolveAgentServiceTicketCount(agent),
            serviceName: service.serviceName,
            serviceAverage,
            avgVsServiceAveragePct:
              agent.avgVsServiceAveragePct ??
              (serviceAverage ? ((agentAverage - serviceAverage) / serviceAverage) * 100 : 0),
            totalServiceTime: agent.totalServiceTime ?? agent.sum,
          },
          branch.branchName,
          branch.branchName,
          service.serviceName
        );
      });
    })
  );

const isAgentServiceTimeReport = (reportTypeId) =>
  (REPORT_KEYS_BY_ID[reportTypeId] || "").includes("branch_agent_service_time_per_service");

export const getDailyTablePinnedColumns = (reportTypeId) =>
  isAgentServiceTimeReport(reportTypeId) ? [AGENT_SERVICE_COLUMN] : [];

export const getDailyTableColumnDefinitions = (reportTypeId, columnDefinitions) => {
  if (!isAgentServiceTimeReport(reportTypeId)) return columnDefinitions;
  return columnDefinitions.filter((column) => column.id !== "agentName");
};

const AgentServiceCell = ({ row }) => (
  <Box>
    <Text>{row.agentName || ""}</Text>
    <Text className="agentServiceMutedCell">{row.serviceName || ""}</Text>
  </Box>
);

const getBranchAgentTicketRows = (data, key) => {
  if (key.includes("tickets_per_agent")) {
    return asArray(data).flatMap((branch) =>
      asArray(branch.agentsData).map((agent) => withScope(agent, branch.branchName, branch.branchName))
    );
  }

  return asArray(data).flatMap((branch) =>
    asArray(branch.servicesData).flatMap((service) =>
      asArray(service.agentsData).map((agent) =>
        withScope(
          {
            ...agent,
            serviceName: service.serviceName,
            percentage: agent.percentage ?? ((agent.total / service.totalServiceData) * 100 || 0),
          },
          branch.branchName,
          branch.branchName,
          service.serviceName
        )
      )
    )
  );
};

export const buildDailyTableRows = ({ reportTypeId, data }) => {
  const key = REPORT_KEYS_BY_ID[reportTypeId] || "";
  const rows = asArray(data);

  if (key.includes("branch_agent_service_time_per_service")) {
    return getBranchAgentServiceRows(rows);
  }

  if (key.includes("branch_agent_tickets_per_service") || key.includes("branch_tickets_per_agent")) {
    return getBranchAgentTicketRows(rows, key);
  }

  if (key.includes("branch_") && key.includes("_per_service")) {
    return getBranchServiceRows(rows);
  }

  if (key.includes("organization_tickets_per_branch")) {
    return rows.map((row) => withScope(row, `${row.branchName} Branch`, row.branchName));
  }

  if (key.includes("branch_arrival_rates_per_hour") || key.includes("branch_customers_in_branch")) {
    return rows.map((row) => withScope(row, row.branchName, row.branchName));
  }

  if (key.includes("organization_")) {
    return rows.map((row) => withScope(row, "Entire Organization"));
  }

  return rows.map((row) => withScope(row, row.branchName || row.scopeName));
};

export const createDailyTableCellRenderer = (reportTypeId) => {
  const key = REPORT_KEYS_BY_ID[reportTypeId] || "";
  const isDuration =
    key.includes("service_time") ||
    key.includes("waiting_time") ||
    key.includes("agent_service_time");

  return (row, columnId) => {
    if (columnId === "scopeName") return row.scopeName || row.branchName || "Entire Organization";
    if (columnId === "agentService") return <AgentServiceCell row={row} />;
    if (columnId === "serviceName") return row.serviceName || row.name || "";
    if (columnId === "agentName") return row.agentName || "";
    if (columnId === "flags") return Array.isArray(row.flags) ? row.flags.join(", ") : row.flags || "";
    if (columnId === "peakHour") return row.peakHour || "";
    if (columnId === "percentage" || columnId === "serviceTimeShare") {
      return pct(row[columnId] ?? row.totalPct);
    }
    if (columnId === "avgVsServiceAveragePct") return pct(row.avgVsServiceAveragePct);
    if (columnId === "totalServiceTime") return formatTime(pickNumber(row.totalServiceTime, row.sum));
    if (columnId === "totalWaitingTime") {
      return formatTime(pickNumber(row.totalWaitingTime, row.waitingTimeInSeconds));
    }
    if (columnId === "serviceAverage") return formatTime(pickNumber(row.serviceAverage));
    if (["median", "p90"].includes(columnId)) {
      return formatTime(pickNumber(row[columnId], row[`${columnId}ServiceTime`]));
    }
    if (["average", "min", "max"].includes(columnId) && isDuration) {
      return formatTime(
        pickNumber(
          row[columnId],
          row.avg,
          row.avgWaitingTimePerTicket,
          row.avgWaitingTimePerHour,
          row.avgWaitingTimePerDay,
          row[`${columnId}WaitingTimePerHour`],
          row[`${columnId}WaitingTimePerDay`]
        )
      );
    }
    if (columnId === "averagePerDay" && isDuration) {
      return formatTime(pickNumber(row.averagePerDay, row.avgWaitingTimePerDay, row.avgWaitingTimePerHour));
    }
    if (columnId === "average") {
      return formatNumber(pickNumber(row.average, row.averageTicketsPerHour, row.avg));
    }
    if (columnId === "averagePerDay") {
      return formatNumber(pickNumber(row.averagePerDay, row.averageTicketsPerDay, row.averageTicketsPerHour));
    }
    if (columnId === "min") return formatNumber(pickNumber(row.min, row.minTicketsPerHour, row.minTicketsPerDay));
    if (columnId === "max") return formatNumber(pickNumber(row.max, row.maxTicketsPerHour, row.maxTicketsPerDay));
    if (columnId === "total") return formatNumber(pickNumber(row.total, row.totalTickets, row.totalVisitors));
    if (columnId === "ticketCount") return formatNumber(pickNumber(row.ticketCount));
    if (columnId === "completed") return formatNumber(pickNumber(row.completed, row.numCompleted));
    if (columnId === "noShow") return formatNumber(pickNumber(row.noShow, row.numNoShow));
    if (columnId === "unattended") return formatNumber(pickNumber(row.unattended, row.numUnattended));
    if (columnId === "abandonmentRate") {
      return formatNumber(pickNumber(row.abandonmentRate, row.abondonmentRate));
    }
    return row[columnId] ?? "";
  };
};

const DailyTableOnlyReport = ({ columnDefinitions, data, renderConfig, reportTypeId }) => {
  const rows = buildDailyTableRows({ reportTypeId, data });
  const pinnedColumns = getDailyTablePinnedColumns(reportTypeId);
  const reportColumns = getDailyTableColumnDefinitions(reportTypeId, columnDefinitions);

  return (
    <Box className="reportContainer dailyTableOnlyReportContainer" mt="20px">
      <Text mb="10px" color="gray.600" fontSize="sm" fontWeight="600">
        Table view
      </Text>
      <ConfiguredReportTable
        pinnedColumns={pinnedColumns}
        columns={reportColumns}
        rows={rows}
        renderConfig={renderConfig}
        renderCell={createDailyTableCellRenderer(reportTypeId)}
      />
    </Box>
  );
};

export default DailyTableOnlyReport;
